import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ReportDialog } from "@/components/ReportDialog";
import { PremiumBadge } from "@/components/PremiumBadge";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { EventThread } from "@/components/EventThread";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, MapPin, Calendar, Check, Heart, Sparkles, Flag } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) +
    " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

const REL_LABEL = {
  sent: { text: "Interest sent", cls: "chip" },
  received: { text: "Wants to go with you", cls: "bg-pink-500/20 text-pink-200 border border-pink-400/30" },
  mutual: { text: "Mutual", cls: "bg-emerald-500/20 text-emerald-200 border border-emerald-400/30" },
  matched: { text: "Matched", cls: "bg-emerald-500/20 text-emerald-200 border border-emerald-400/30" },
};

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [active, setActive] = useState(null);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [matchOverlay, setMatchOverlay] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: ev } = useQuery({ queryKey: ["event", id], queryFn: async () => (await api.get(`/events/${id}`)).data });
  const { data: attendees = [], refetch } = useQuery({ queryKey: ["attendees", id], queryFn: async () => (await api.get(`/events/${id}/attendees`)).data });
  const shownAttendees = verifiedOnly ? attendees.filter((u) => u.verified) : attendees;

  const join = async () => {
    await api.post(`/events/${id}/join`);
    // Refresh the event (joined flag) AND the attendee grid so unlocked profiles appear instantly.
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["event", id] }),
      refetch(),
    ]);
    qc.invalidateQueries({ queryKey: ["events"] });
    qc.invalidateQueries({ queryKey: ["matches"] });
    toast.success("You're on the list");
  };

  const expressInterest = async (u) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${id}/interest`, { to_user_id: u.user_id });
      await refetch();
      qc.invalidateQueries({ queryKey: ["event", id] });
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["matches"] });
      if (data.matched) {
        setActive(null);
        setMatchOverlay({ partner: u, match: data.match });
      } else {
        toast.success(`Interest sent to ${u.name}`);
      }
    } catch { toast.error("Could not send interest"); }
    finally { setBusy(false); }
  };

  if (!ev) return <div className="h-96 rounded-2xl bg-white/5 animate-pulse" />;

  return (
    <div className="fade-up">
      <button data-testid="back-btn" onClick={() => navigate("/discover")} className="flex items-center gap-2 text-emerald-100/70 hover:text-white mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="relative rounded-3xl overflow-hidden border border-amber-500/20">
        <img src={imgUrl(ev.cover_image)} alt={ev.title} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"; }} className="h-72 sm:h-96 w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0B130E, rgba(11,19,14,.3) 55%, transparent)" }} />
        <div className="absolute bottom-0 p-6 sm:p-8">
          <span className="chip inline-block text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full">{ev.category}</span>
          {ev.great_for_dates && (
            <span data-testid="event-great-badge" className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-400 text-emerald-950">
              ♥ Great for dates
            </span>
          )}
          <h1 className="font-display font-black text-3xl sm:text-5xl mt-3">{ev.title}</h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-sm text-emerald-50/80">
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-amber-300" /> {fmtDate(ev.date_time)}</span>
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-amber-300" /> {ev.venue}, {ev.city}</span>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 mt-5 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-emerald-100/70 max-w-xl leading-relaxed">{ev.description}</p>
        {ev.ticket_url?.startsWith("https://") && (
          <a href={ev.ticket_url} target="_blank" rel="noopener noreferrer" data-testid="event-ticket-link"
            className="inline-flex mt-3 text-sm font-semibold text-amber-300 hover:text-amber-200">
            Get tickets on {ev.source === "skiddle" ? "Skiddle" : "Ticketmaster"} →
          </a>
        )}
        {ev.joined ? (
          <span className="flex items-center gap-2 text-emerald-300 text-sm font-semibold shrink-0"><Check className="h-4 w-4" /> You're interested</span>
        ) : (
          <Button data-testid="join-event-btn" onClick={join} className="btn-gold rounded-full h-11 px-6 shrink-0">I'm interested</Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-8 mb-4">
        <h2 className="font-display font-bold text-2xl">Who's going</h2>
        <span data-testid="attendee-count" className="text-sm text-emerald-100/50">{shownAttendees.length} {verifiedOnly ? "verified" : "people"}</span>
        <label className="ml-auto flex items-center gap-2 text-sm cursor-pointer select-none">
          <VerifiedBadge size="xs" /> Verified only
          <Switch data-testid="verified-only-toggle" checked={verifiedOnly} onCheckedChange={setVerifiedOnly} />
        </label>
      </div>

      {shownAttendees.length === 0 ? (
        <div data-testid="attendees-empty" className="glass rounded-2xl p-10 text-center text-emerald-100/60">
          {verifiedOnly && attendees.length > 0 ? "No verified attendees yet — turn off the filter to see everyone going." : "Be the first — tap \"I'm interested\" and check back soon."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {shownAttendees.map((u, i) => (
            <button key={u.user_id} data-testid={`attendee-${u.user_id}`} onClick={() => setActive(u)}
              className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-amber-500/40 transition-all aspect-[3/4] fade-up"
              style={{ animationDelay: `${Math.min(i * 0.04, 0.3)}s` }}>
              <img src={imgUrl(u.photos?.[0] || u.picture)} alt={u.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(11,19,14,.95), transparent 55%)" }} />
              {REL_LABEL[u.relation] && (
                <span className={`absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${REL_LABEL[u.relation].cls}`}>
                  {REL_LABEL[u.relation].text}
                </span>
              )}
              {u.premium && <span className="absolute top-2 right-2"><PremiumBadge size="xs" showLabel={false} /></span>}
              <div className="absolute bottom-0 p-3 text-left">
                <p className="font-display font-bold leading-tight flex items-center gap-1">{u.name}{u.age ? `, ${u.age}` : ""} {u.verified && <VerifiedBadge size="xs" />}</p>
                <p className="text-xs text-emerald-100/60">{u.city}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      <EventThread eventId={ev.id} joined={ev.joined} />

      {/* Attendee full-screen card */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="glass border-amber-500/20 p-0 overflow-hidden max-w-md">
          <DialogTitle className="sr-only">{active?.name || "Attendee"} profile</DialogTitle>
          {active && (
            <div>
              <div className="relative aspect-[4/5]">
                <img src={imgUrl(active.photos?.[0] || active.picture)} alt={active.name} className="h-full w-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0B130E, transparent 55%)" }} />
                <div className="absolute bottom-0 p-5">
                  <h3 className="font-display font-black text-3xl flex items-center gap-2">{active.name}{active.age ? `, ${active.age}` : ""} {active.verified && <VerifiedBadge showLabel />} {active.premium && <PremiumBadge size="sm" />}</h3>
                  <p className="text-sm text-emerald-100/70 flex items-center gap-1.5 mt-1"><MapPin className="h-3.5 w-3.5" /> {active.city}, {active.country}</p>
                </div>
              </div>
              <div className="p-5">
                {active.bio && <p className="text-sm text-emerald-100/80 leading-relaxed">{active.bio}</p>}
                {active.interests?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {active.interests.map((it) => <span key={it} className="text-xs chip px-3 py-1 rounded-full">{it}</span>)}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-6">
                  {active.relation === "matched" ? (
                    <Button data-testid="go-to-match" onClick={() => navigate(`/call/${active.match_id}`)} className="flex-1 btn-gold rounded-full h-12">
                      <Sparkles className="h-4 w-4 mr-2" /> Start vibe check
                    </Button>
                  ) : active.relation === "sent" ? (
                    <Button disabled className="flex-1 rounded-full h-12 bg-white/10 text-emerald-100/60">Interest sent ✓</Button>
                  ) : (
                    <Button data-testid="express-interest-btn" disabled={busy} onClick={() => expressInterest(active)} className="flex-1 btn-pink rounded-full h-12">
                      <Heart className="h-4 w-4 mr-2" /> {active.relation === "received" ? "Match back" : "Go together"}
                    </Button>
                  )}
                  <ReportDialog reportedId={active.user_id}
                    trigger={<Button variant="outline" size="icon" className="rounded-full h-12 w-12 border-white/15 text-pink-300"><Flag className="h-4 w-4" /></Button>}
                    onBlocked={() => { setActive(null); refetch(); }} />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* It's a match overlay */}
      <Dialog open={!!matchOverlay} onOpenChange={(o) => !o && setMatchOverlay(null)}>
        <DialogContent className="glass border-amber-500/30 max-w-sm text-center p-8">
          <DialogTitle className="sr-only">It's a match</DialogTitle>
          {matchOverlay && (
            <div className="pop">
              <div className="flex justify-center -space-x-4 mb-5">
                <img src={imgUrl(matchOverlay.partner.photos?.[0] || matchOverlay.partner.picture)} className="h-20 w-20 rounded-full object-cover ring-4 ring-pink-400/50" alt="them" />
                <div className="h-20 w-20 rounded-full bg-emerald-500/20 ring-4 ring-amber-400/50 flex items-center justify-center">
                  <Heart className="h-8 w-8 text-pink-400 pulse-ring rounded-full" />
                </div>
              </div>
              <h3 className="font-display font-black text-3xl gold-text">It's a match!</h3>
              <p className="text-sm text-emerald-100/70 mt-2">
                You and {matchOverlay.partner.name} both want to go. Time for your mandatory 60-second vibe check.
              </p>
              <Button data-testid="start-vibe-check" onClick={() => navigate(`/call/${matchOverlay.match.id}`)} className="w-full btn-gold rounded-full h-12 mt-6">
                Start 60-second call
              </Button>
              <button onClick={() => setMatchOverlay(null)} className="text-xs text-emerald-100/50 mt-3 hover:text-emerald-100/80">Later</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
